import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message, Modal, Select, Tag } from 'antd';
import { api } from '../../api/client';
import { useEvalRoleLabels } from '../../contexts/EvalRoleLabelsContext';
import { useTalentPoolLabels } from '../../contexts/TalentPoolLabelsContext';
import type { EvaluatorRef, PivotRow } from './evalRelationsPivot';
import { POOL_ROLE_OPTIONS, type ColDef } from './poolColDefs';

export interface EditTarget {
  row: PivotRow;
  col: ColDef;
  current: EvaluatorRef;
}

export interface CreateTarget {
  row: PivotRow;
  col: ColDef;
  defaultEvaluatorId?: number;
  defaultRole: string;
}

interface EmployeeOption {
  id: number;
  name: string;
  position?: string;
  deptPath?: string;
  talentPool: string;
}

interface Props {
  batchId: number | undefined;
  editTarget: EditTarget | null;
  createTarget: CreateTarget | null;
  onCloseEdit: () => void;
  onCloseCreate: () => void;
}

export function EvalRelationsEditModals({
  batchId,
  editTarget,
  createTarget,
  onCloseEdit,
  onCloseCreate,
}: Props) {
  const queryClient = useQueryClient();
  const { getEvalRoleLabel, enabledCodes } = useEvalRoleLabels();
  const { getTalentPoolColor } = useTalentPoolLabels();
  const [editPickId, setEditPickId] = useState<number | undefined>();
  const [editRolePick, setEditRolePick] = useState<string | undefined>();
  const [createPickId, setCreatePickId] = useState<number | undefined>(
    createTarget?.defaultEvaluatorId,
  );
  const [createRolePick, setCreateRolePick] = useState<string | undefined>(
    createTarget?.defaultRole,
  );

  const { data: qualityTemplates = [] } = useQuery<
    Array<{ pool: string; relationWeights: Record<string, number> }>
  >({
    queryKey: ['quality-templates', batchId],
    queryFn: () => api.get('/admin/quality-templates', { params: { batchId } }),
    enabled: !!batchId,
  });

  const poolAllowedRoles = useMemo(() => {
    const map: Record<string, string[]> = { ...POOL_ROLE_OPTIONS };
    qualityTemplates.forEach((template) => {
      const roles = Object.keys(template.relationWeights || {});
      if (roles.length) map[template.pool] = roles;
    });
    return map;
  }, [qualityTemplates]);

  const buildRoleOptions = (pool: string) => {
    // 字典停用的角色全局不可选（enabledCodes 为空 = 字典未加载，跳过过滤）
    const raw = poolAllowedRoles[pool] || POOL_ROLE_OPTIONS.BACKEND;
    const recommended = enabledCodes.length
      ? raw.filter((role) => enabledCodes.includes(role))
      : raw;
    const others = (enabledCodes.length ? enabledCodes : recommended).filter(
      (role) => !recommended.includes(role),
    );
    const toOption = (role: string) => ({
      value: role,
      label: `${getEvalRoleLabel(pool, role)}（${role}）`,
    });
    const groups: Array<{ label: ReactNode; options: ReturnType<typeof toOption>[] }> = [];
    if (recommended.length) {
      groups.push({
        label: <span style={{ color: '#1677ff' }}>本池模板允许的角色</span>,
        options: recommended.map(toOption),
      });
    }
    if (others.length) {
      groups.push({
        label: <span style={{ color: '#fa8c16' }}>其他角色（不在本池模板中，权重可能为 0）</span>,
        options: others.map(toOption),
      });
    }
    return groups;
  };

  const { data: employeePage } = useQuery<{ items: EmployeeOption[] }>({
    queryKey: ['admin-employees-pick'],
    queryFn: () =>
      api.get('/admin/employees', { params: { status: 'ACTIVE', page: 1, size: 500 } }),
    enabled: editTarget !== null || createTarget !== null,
  });

  const pickEmpOptions = useMemo(() => {
    const activePool = editTarget?.row.pool || createTarget?.row.pool;
    if (!activePool) return [];
    return [...(employeePage?.items || [])]
      .sort((a, b) => {
        if (a.talentPool === activePool && b.talentPool !== activePool) return -1;
        if (a.talentPool !== activePool && b.talentPool === activePool) return 1;
        return (a.name || '').localeCompare(b.name || '', 'zh-CN');
      })
      .map((employee) => ({
        label: `${employee.name}${employee.position ? ` · ${employee.position}` : ''}${
          employee.deptPath ? ` · ${employee.deptPath}` : ''
        }`,
        value: employee.id,
      }));
  }, [employeePage, editTarget, createTarget]);

  const closeEdit = () => {
    setEditPickId(undefined);
    setEditRolePick(undefined);
    onCloseEdit();
  };
  const closeCreate = () => {
    setCreatePickId(undefined);
    setCreateRolePick(undefined);
    onCloseCreate();
  };

  const createMutation = useMutation({
    mutationFn: (variables: {
      batchId: number;
      evaluatorId: number;
      evaluateeId: number;
      roleInEval: string;
    }) => api.post('/admin/eval-tasks', variables),
    onSuccess: () => {
      message.success('补派成功，已写入派单表');
      queryClient.invalidateQueries({ queryKey: ['eval-grouped-by-evaluator'] });
      queryClient.invalidateQueries({ queryKey: ['eval-tasks-admin'] });
      closeCreate();
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || '补派失败');
    },
  });

  const reassignMutation = useMutation({
    mutationFn: (variables: {
      taskId: number;
      newEvaluatorId?: number;
      newRoleInEval?: string;
    }) =>
      api.post(`/admin/eval-tasks/${variables.taskId}/reassign`, {
        newEvaluatorId: variables.newEvaluatorId,
        newRoleInEval: variables.newRoleInEval,
      }),
    onSuccess: () => {
      message.success('改派成功，已同步到「素质项评价派单」与「素质项评价进度」');
      queryClient.invalidateQueries({ queryKey: ['eval-grouped-by-evaluator'] });
      queryClient.invalidateQueries({ queryKey: ['eval-tasks-admin'] });
      closeEdit();
    },
    onError: (error: any) => message.error(error?.message || '改派失败'),
  });

  return (
    <>
      <Modal
        title="改派评价人 / 评价关系"
        open={editTarget !== null}
        onCancel={closeEdit}
        onOk={() => {
          if (!editTarget) return;
          const evaluatorChanged =
            editPickId != null && editPickId !== editTarget.current.evaluatorId;
          const roleChanged =
            editRolePick != null && editRolePick !== editTarget.current.roleInEval;
          if (!evaluatorChanged && !roleChanged) {
            message.warning('未变更，请至少修改评价人或评价关系其中之一');
            return;
          }
          reassignMutation.mutate({
            taskId: editTarget.current.taskId,
            newEvaluatorId: evaluatorChanged ? editPickId : undefined,
            newRoleInEval: roleChanged ? editRolePick : undefined,
          });
        }}
        okButtonProps={{ loading: reassignMutation.isPending }}
        okText="改派"
        cancelText="取消"
        destroyOnClose
        width={600}
      >
        {editTarget && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ color: '#888', fontSize: 12 }}>被评价人</div>
              <div>
                <strong>{editTarget.row.evaluatee.name}</strong>
                <span style={{ color: '#999', marginLeft: 8 }}>
                  {editTarget.row.evaluatee.position || '—'} · {editTarget.row.evaluatee.deptPath || '—'}
                </span>
              </div>
            </div>
            <div>
              <div style={{ color: '#888', fontSize: 12 }}>当前所在列</div>
              <Tag color={getTalentPoolColor(editTarget.row.pool)}>{editTarget.col.label}</Tag>
              <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>
                taskId: {editTarget.current.taskId} · 当前关系：
                {getEvalRoleLabel(editTarget.row.pool, editTarget.current.roleInEval)}
              </span>
            </div>
            <div><div style={{ color: '#888', fontSize: 12 }}>当前评价人</div><Tag>{editTarget.current.evaluatorName}</Tag></div>
            <div>
              <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>改派为（评价人）</div>
              <Select
                showSearch
                allowClear
                placeholder="不改的话留空；要改派则搜索员工姓名 / 职位 / 部门"
                value={editPickId}
                onChange={setEditPickId}
                options={pickEmpOptions}
                filterOption={(input, option) => String(option?.label || '').toLowerCase().includes(input.toLowerCase())}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>评价关系改为</div>
              <Select
                allowClear
                placeholder="不改的话留空；要改关系则选新角色（自评、直属、下属、其他领导… 全部 8 种都可选）"
                value={editRolePick}
                onChange={setEditRolePick}
                style={{ width: '100%' }}
                options={buildRoleOptions(editTarget.row.pool)}
              />
              <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                改派会把该任务的状态重置为「待提交」并清空草稿。改了关系后，本条记录会自动跳到对应列。
                选了「其他角色」（不在本池模板）时，模板的关系权重可能为 0，请确认是要换关系还是要换池。
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="补派评价人"
        open={createTarget !== null}
        onCancel={closeCreate}
        afterOpenChange={(isOpen) => {
          if (isOpen && createTarget) {
            setCreatePickId(createTarget.defaultEvaluatorId);
            setCreateRolePick(createTarget.defaultRole);
          }
        }}
        onOk={() => {
          if (!createTarget) return;
          if (!batchId) return void message.error('请先选择批次');
          if (!createPickId) return void message.warning('请选择评价人');
          if (!createRolePick) return void message.warning('请选择评价关系');
          const evaluateeId = createTarget.row.evaluatee.id;
          if (createRolePick === 'SELF' && createPickId !== evaluateeId) {
            return void message.error('自评（SELF）的评价人必须是被评价人本人');
          }
          if (createRolePick !== 'SELF' && createPickId === evaluateeId) {
            return void message.error('评价人与被评价人不能为同一人（仅自评 SELF 允许）');
          }
          createMutation.mutate({
            batchId,
            evaluatorId: createPickId,
            evaluateeId,
            roleInEval: createRolePick,
          });
        }}
        okButtonProps={{ loading: createMutation.isPending }}
        okText="补派"
        cancelText="取消"
        destroyOnClose
        width={600}
      >
        {createTarget && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ color: '#888', fontSize: 12 }}>被评价人</div>
              <div><strong>{createTarget.row.evaluatee.name}</strong><span style={{ color: '#999', marginLeft: 8 }}>{createTarget.row.evaluatee.position || '—'} · {createTarget.row.evaluatee.deptPath || '—'}</span></div>
            </div>
            <div>
              <div style={{ color: '#888', fontSize: 12 }}>补派到列</div>
              <Tag color={getTalentPoolColor(createTarget.row.pool)}>{createTarget.col.label}</Tag>
              <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>
                该列允许的关系：{createTarget.col.roles.map((role) => getEvalRoleLabel(createTarget.row.pool, role)).join(' / ')}
              </span>
            </div>
            <div>
              <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>评价关系</div>
              <Select
                value={createRolePick}
                onChange={(role) => {
                  setCreateRolePick(role);
                  if (role === 'SELF') setCreatePickId(createTarget.row.evaluatee.id);
                  else if (createPickId === createTarget.row.evaluatee.id) setCreatePickId(undefined);
                }}
                style={{ width: '100%' }}
                options={buildRoleOptions(createTarget.row.pool)}
              />
              <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                默认按列定义预选；多 role 列（如「其他领导/下属」）可手动切换到下属或其他领导。
              </div>
            </div>
            <div>
              <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>评价人</div>
              <Select
                showSearch
                placeholder={createRolePick === 'SELF' ? '自评固定为被评价人本人' : '搜索员工姓名 / 职位 / 部门'}
                value={createPickId}
                onChange={setCreatePickId}
                options={pickEmpOptions}
                disabled={createRolePick === 'SELF'}
                filterOption={(input, option) => String(option?.label || '').toLowerCase().includes(input.toLowerCase())}
                style={{ width: '100%' }}
              />
              <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                提交后会按被评价人的人才池自动匹配模板，不需要手选；若该池无模板会报错。
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
